import { BookOpen, Users, CheckSquare, Award, ClipboardCheck, Calendar, ShieldCheck, HelpCircle } from 'lucide-react';
import { Card, SectionTitle } from '@/components/ui';

export function TrainerGuideView() {
  return (
    <div className="space-y-6 animate-fade-in max-w-5xl mx-auto pb-12">
      <SectionTitle
        title="Trainer Guide & Onboarding Overview"
        subtitle="Welcome to the Uisce Éireann Commercial Contract Onboarding Management System."
        icon={<BookOpen className="w-6 h-6" />}
      />

      {/* Welcome Banner */}
      <Card className="p-6 bg-gradient-to-r from-blue-900 to-slate-900 text-white border-none shadow-md">
        <h3 className="text-xl font-bold mb-2">Uisce Éireann Customer Operations Training Platform</h3>
        <p className="text-sm text-slate-300 leading-relaxed max-w-3xl">
          This portal streamlines trainee management across the 15-day onboarding curriculum and 3-day floor hypercare period. Track real-time attendance, core module scores, weekly RAG evaluations, and compliance progress.
        </p>
      </Card>

      {/* Module Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-5 space-y-2">
          <div className="flex items-center gap-2.5 text-primary-600 font-bold text-sm">
            <Users className="w-5 h-5" /> Dashboard & Roster Management
          </div>
          <p className="text-xs text-slate-600 leading-relaxed">
            Add new trainees, generate PIN codes for agent logins, specify Irish-speaking attributes, and manage cohort start dates.
          </p>
        </Card>

        <Card className="p-5 space-y-2">
          <div className="flex items-center gap-2.5 text-primary-600 font-bold text-sm">
            <CheckSquare className="w-5 h-5" /> Daily Attendance & Hypercare
          </div>
          <p className="text-xs text-slate-600 leading-relaxed">
            Log daily attendance across 18 working days (15 training days + 3 hypercare days). Click to cycle status: Blank → Present (Tick) → Absent (Cross) → Blank.
          </p>
        </Card>

        <Card className="p-5 space-y-2">
          <div className="flex items-center gap-2.5 text-primary-600 font-bold text-sm">
            <Award className="w-5 h-5" /> Assessments & Accreditation
          </div>
          <p className="text-xs text-slate-600 leading-relaxed">
            Record module percentage scores (85% pass threshold) across Direct Debit, Customer Contact, Metering, and CRU Complaints. Manage 2 written attempts and 3 call accreditation attempts.
          </p>
        </Card>

        <Card className="p-5 space-y-2">
          <div className="flex items-center gap-2.5 text-primary-600 font-bold text-sm">
            <ClipboardCheck className="w-5 h-5" /> Weekly RAG Evaluations
          </div>
          <p className="text-xs text-slate-600 leading-relaxed">
            Assign Green, Amber, or Red performance ratings alongside detailed trainer feedback notes visible directly on trainee portals.
          </p>
        </Card>
      </div>

      {/* Role Privileges Overview */}
      <Card className="p-6 space-y-4">
        <h4 className="font-bold text-sm text-slate-900 border-b border-slate-100 pb-2">Platform User Access Levels</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
          <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100 space-y-1">
            <div className="font-bold text-slate-800">Trainer Account</div>
            <p className="text-slate-500">Full write, edit, create, and delete privileges across all views and rosters.</p>
          </div>
          <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100 space-y-1">
            <div className="font-bold text-slate-800">Guest / Manager Mode</div>
            <p className="text-slate-500">Read-only overview for management auditing without editing rights.</p>
          </div>
          <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100 space-y-1">
            <div className="font-bold text-slate-800">Trainee / Agent Portal</div>
            <p className="text-slate-500">Personal view showing individual scores, attendance rates, and trainer notes.</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
